import { Injectable } from '@angular/core';
import { LoginInfo } from '../model/login';

const LOGIN_INFO_KEY = 'login-info';

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {

  logout(): void {
    window.sessionStorage.clear();
  }

  public saveLoginInfo(loginInfo: LoginInfo): void {
    window.sessionStorage.removeItem(LOGIN_INFO_KEY);
    window.sessionStorage.setItem(LOGIN_INFO_KEY, JSON.stringify(loginInfo));
  }

  public getToken(): string | undefined {
    const loginInfo = this.getLoginInfo();

    if (!loginInfo) {
      return undefined;
    }

    if (!loginInfo.token) {
      this.logout();
      return undefined;
    }

    if (!this.isValidToken(loginInfo.token)) {
      this.logout();
      return undefined;
    }

    return loginInfo.token;
  }

  public getLoginInfo(): LoginInfo | undefined {
    const loginInfo = window.sessionStorage.getItem(LOGIN_INFO_KEY);

    if (!loginInfo) {
      return undefined;
    }

    try {
      return JSON.parse(loginInfo);
    } catch {
      this.logout();
      return undefined;
    }
  }

  public isAuthenticated(): boolean {
    return !!this.getToken();
  }

  private isValidToken(token: string): boolean {
    if (!token || !this.isJwt(token)) {
      return false;
    }

    return !this.isTokenExpired(token);
  }

  private isJwt(token: string): boolean {
    return token.split('.').length === 3;
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = this.getJwtPayload(token);

      if (!payload?.exp) {
        return true;
      }

      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  private getJwtPayload(token: string): any {
    const payloadBase64 = token.split('.')[1];

    const normalizedPayload = payloadBase64
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + (4 - normalizedPayload.length % 4) % 4,
      '='
    );

    const decodedPayload = atob(paddedPayload);

    return JSON.parse(decodedPayload);
  }
}
